import { db } from '../../db/db'
import { debugLog } from '@core/utils/log'
import {
  META_DEVICE_TOKEN,
  META_ACTIVE_VERSION,
  META_ACTIVE_SHIFT_ID,
  META_CATALOG_VERSION,
  META_CATALOG_SYNCED_AT,
  META_CATALOG_RESYNC,
  META_DEVICE_ID,
  META_ZONE_ID
} from '../../db/schema'
import type {
  Category,
  FiscalReceiptData,
  PosShift,
  Product,
  QueuedSale,
  StockMovement,
  Writeoff
} from '../../db/models'
import type { EventBus } from '../../bus/EventBus'
import { PosEvent } from '../../bus/events'
import { generateUUID, isValidUUID } from '../../utils/crypto'
import { parsePushRejection } from './pushResponse'
import { autoCloseStaleShift } from '../shift/shiftFreshness'
import {
  failPendingEdit,
  listPendingEdits,
  pendingEditMap,
  removePendingEdits,
  type SentEdit
} from '../catalog/pendingProductEdits'
import { EDITABLE_FIELDS } from '../catalog/pendingEditLogic'

/** 65.14/9c: решение облака по hold-чеку. */
export interface MarkingDecision {
  receiptId: string
  decision: 'approve' | 'cancel'
  cancelMode?: 'refund' | 'writeoff'
  comment?: string
  decidedBy?: string
  decidedAt?: string
  status?: string
  totalAmount: number
  applyAttempts?: number
}

export type MarkingDecisionApplyResult =
  | { ok: true }
  | { ok: false; reason: string }

interface MarkingDecisionDto {
  receipt_id: string
  status: string
  total_amount: string | number
  marking_decision: 'approve' | 'cancel'
  marking_cancel_mode: 'refund' | 'writeoff' | null
  marking_comment: string | null
  marking_decided_by: string | null
  marking_decided_at: string | null
  marking_apply_attempts?: number
}

/** ms epoch → ISO для timestamp-колонок облака (65.14). */
function toIso(value: number | undefined): string | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? new Date(value).toISOString()
    : undefined
}

/** Безопасный разбор JSON-строки Z-отчёта (не роняем синк на битых данных). */
function safeParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/** Полезная нагрузка одного чека для PUSH в облако. */
interface SyncReceiptPayload {
  id: string
  device_id: string
  total_amount: number
  items: unknown[]
  sold_at: string
  /**
   * Фискальный статус чека: 'fiscalized' | 'non_fiscal' | 'offline_pending'.
   * Облако хранит его в receipts.status — нужен для раздельного фискального
   * Z-отчёта (нефискальные чеки не попадают в фискальную выручку).
   */
  status: string
  /** Зона (рабочее место) смены — для облачного Z по зоне. */
  zone_id?: string
  /** Магазин/торговая точка (аудит). */
  store_id?: string
  /** Номер смены зоны. */
  shift_number?: number
  /** Имя кассира (разбивка Z по кассирам). */
  cashier_name?: string
  /** Тип документа: 'sale' | 'correction' (исключается из Z-выручки). */
  doc_type?: string
  /** Способ оплаты: 'cash' | 'card' | 'sbp' (ARCH-TASK-62, разрез в отчётах). */
  payment_method?: string
  /** Признак расчёта (ARCH-TASK-65): возврат приходит с отрицательной суммой. */
  settlement_type?: string
  /** Исходный чек возврата (ARCH-TASK-65.5); нет — возврат без чека. */
  original_receipt_id?: string
  /** Внутренний номер офлайн-документа (не ККТ). */
  offline_receipt_number?: string
  /** 65.10: продажа маркированного без кода (по праву) / invalid код. */
  marking_skipped?: boolean
  marking_invalid?: boolean
  marking_reason?: string
  /** Статус проверки кодов в ЧЗ (65.12). */
  cz_status?: string
  /** 65.14: hold-решение и его применение на кассе. */
  marking_decision?: 'approve' | 'cancel' | 'auto'
  marking_decision_source?: 'pos' | 'cloud' | 'auto'
  marking_decided_by?: string
  marking_decided_at?: string
  marking_applied_at?: string
  marking_auto_at?: string
  marking_comment?: string
  /** 65.14: стадия печати hold-чека для облачного мониторинга. */
  marking_print_state?: string
  marking_print_error?: string
}

/**
 * Полный Z-отчёт смены по ЗОНЕ (агрегируется в облаке по всем устройствам).
 * Разбивка: фискальные / отложенные (offline_pending) / нефискальные чеки.
 */
/** Чек из общей очереди дофискализации зоны. */
export interface RemoteFiscalizationItem {
  queueId: number
  receiptId: string
  zoneId?: string
  shiftNumber?: number
  deviceId: string
  attempts: number
  items: Array<{
    name: string
    price: number
    quantity: number
    unit?: string
    marking_code?: string
    barcode?: string
  }>
  totalAmount: number
  paymentMethod?: string
  docType?: 'sale' | 'correction'
  settlementType?: 'income' | 'income_return' | 'outcome' | 'outcome_return'
  cashierName?: string
  originalReceiptId?: string
}

interface FiscalizationQueueResponse {
  queue_id: string | number
  receipt_id: string
  zone_id: string | null
  shift_number: number | null
  device_id: string
  attempts: number
  items: RemoteFiscalizationItem['items']
  total_amount: string | number
  payment_method: string | null
  doc_type: string | null
  settlement_type: string | null
  cashier_name: string | null
  original_receipt_id: string | null
}

interface RemoteReceiptStatus {
  id: string
  status: string
  fiscal_data: Partial<FiscalReceiptData> | null
  fiscalized_at: string | null
  offline_receipt_number?: string | null
}


export interface ShiftZReport {
  zone_id: string
  shift_number: number
  fiscal_total: number
  deferred_total: number
  non_fiscal_total: number
  /** 65.12: чеки, ждущие решения по маркировке (деньги в ящике). */
  held_total?: number
  held_count?: number
  /** 9d: состояние hold-очереди за смену. */
  cancelled_total?: number
  cancelled_count?: number
  valid_ready_count?: number
  printed_count?: number
  print_failures_count?: number
  hold_aging_max_sec?: number
  blocked?: boolean
  sales_count: number
  /** Возвраты смены (ARCH-TASK-65): сумма положительная, в итог входит со знаком «−». */
  returns_total?: number
  returns_count?: number
  total_in_drawer: number
  /** Итог с коррекциями: продажи + коррекции прихода − коррекции-возвраты. */
  total_with_corrections?: number
  /** Чеки коррекции (в выручку продаж не входят). */
  corrections_total?: number
  corrections_count?: number
  /** Коррекции прихода и возврата прихода — раздельно (ARCH-TASK-65.6). */
  corrections_income_total?: number
  corrections_income_count?: number
  corrections_returns_total?: number
  corrections_returns_count?: number
  by_device: Array<{ device_id: string; total: number; count: number }>
  by_cashier: Array<{ cashier_name: string | null; total: number; count: number }>
}

/** Ответ сервера на PUSH чеков. */
interface PushResponse {
  success: boolean
  processed: number
  duplicates: number
  total: number
}

/** Товар, приходящий из облачной дельты каталога. */
interface CatalogProductDto {
  id: number
  barcode: string
  name: string
  price: string | number
  stock: string | number
  /** Минимальный остаток; null — общий порог магазина (ARCH-TASK-64a). */
  min_stock?: string | number | null
  requires_marking: boolean
  is_created_on_pos: boolean
  category?: string
  /** Верхний уровень дерева категорий (денормализовано). */
  category_parent?: string | null
  /** Порядковый номер плитки внутри категории. */
  category_ordinal?: number | null
  /** Активен ли товар для продажи. */
  is_active?: boolean
  /** Дополнительные таксономии источника. */
  source_meta?: Record<string, unknown> | null
  subcategory?: string
  is_weight?: boolean
  unit?: string
  is_deleted?: boolean
  /** Клиентский UUID заявки с кассы (null для товаров из импорта). */
  client_uuid?: string | null
  catalog_version_id: number | null
}

/** Категория дерева из облака. */
interface CatalogCategoryDto {
  name: string
  parent_name: string | null
  department: string | null
  is_active: boolean
  sort_order: number | null
  image_url: string | null
  has_image?: boolean
}

/** Ответ сервера на PULL дельты каталога. */
interface CatalogResponse {
  latest_version: number
  up_to_date: boolean
  /** метка времени сервера (time-based дельта, ARCH-TASK-59). */
  server_time?: string
  /** составной курсор следующей страницы дельты. */
  cursor?: { since: string; id: number } | null
  products: CatalogProductDto[]
  /** Дерево категорий tenant-level (отдаётся целиком). */
  categories?: CatalogCategoryDto[]
}

/** Вскрытая тара для PUSH в облако (мониторинг директора). */
interface ContainerPushPayload {
  device_id: string
  /** Зона/рабочее место — ключ общего пула тары. */
  zone_id: string
  barcode: string
  name: string
  datamatrix: string
  initial_weight: number
  remaining_weight: number
  status: 'active' | 'exhausted'
  opened_at: string
}

/** Смена из облачного журнала (реконсиляция закрытий и присоединение к зоне). */
interface OpenShiftResult {
  id: string
  shift_id?: string
  shift_number: number
  opened_at: string
  opened_by_device?: string | null
}

interface ShiftRowDto {
  id: string
  device_id: string
  zone_id: string | null
  store_id: string | null
  opened_by_device: string | null
  shift_number: number
  cashier_name: string
  status: 'open' | 'closed'
  fiscalization_enabled: boolean
  initial_cash: string | number
  total_sales: string | number
  sales_count: number
  z_report_data: unknown
  opened_at: string
  closed_at: string | null
}

/** Вскрытая тара из облака (PULL общего пула зоны). */
interface ContainerDto {
  device_id: string | null
  zone_id: string | null
  barcode: string
  name: string
  datamatrix: string
  initial_weight: string | number
  remaining_weight: string | number
  status: 'active' | 'exhausted'
  opened_at: string
}

/** Кассир, приходящий из облака (/auth/pos/pins). */
interface CashierDto {
  user_id: string
  name: string
  pin_hash: string
  role?: 'admin' | 'cashier'
  permissions?: { can_create_on_pos: boolean; can_manage_shifts: boolean }
}

/**
 * Фоновый сервис синхронизации кассы с облаком.
 *
 * Цикл раз в N секунд (по умолчанию 60): PUSH неотправленных чеков из
 * sales_queue, затем PULL инкрементальной дельты каталога и справочника
 * кассиров (для оффлайн-PIN). Работает только при наличии сети и токена,
 * не допускает параллельных прогонов (isSyncing-гард).
 */
/** Перекрытие окна дельты каталога: страховка от гонки watermark-а. */
const CATALOG_LOOKBACK_MS = 5000

/**
 * Метка разового полного пересинка каталога. Меняется при исправлениях, после
 * которых устройство обязано перечитать все строки (ARCH-TASK-61.0c).
 */
const CATALOG_RESYNC_MARKER = '2026-10-19-delete-matching'

/**
 * Сдвиг ISO-метки времени. Миллисекундная точность здесь безопасна: любой сдвиг
 * делается В ПРОШЛОЕ, то есть только расширяет выборку (лишнее отфильтруется
 * сравнением с локальной копией).
 */
function shiftIso(iso: string, deltaMs: number): string {
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? iso : new Date(t + deltaMs).toISOString()
}

/** Совпадает ли локальная копия товара с пришедшей из облака (без updatedAt). */
function isSameProduct(a: Product, b: Product): boolean {
  return (
    a.cloudId === b.cloudId &&
    a.catalogVersionId === b.catalogVersionId &&
    a.barcode === b.barcode &&
    a.name === b.name &&
    a.price === b.price &&
    (a.minStock ?? null) === (b.minStock ?? null) &&
    a.stock === b.stock &&
    a.unit === b.unit &&
    a.category === b.category &&
    a.categoryParent === b.categoryParent &&
    a.categoryOrdinal === b.categoryOrdinal &&
    (a.isActive !== false) === (b.isActive !== false) &&
    JSON.stringify(a.sourceMeta ?? null) === JSON.stringify(b.sourceMeta ?? null) &&
    a.subcategory === b.subcategory &&
    Boolean(a.isWeight) === Boolean(b.isWeight) &&
    Boolean(a.requiresMarking) === Boolean(b.requiresMarking) &&
    Boolean(a.isCreatedOnPos) === Boolean(b.isCreatedOnPos) &&
    (a.clientUuid ?? null) === (b.clientUuid ?? null) &&
    (a.cloudStatus ?? null) === (b.cloudStatus ?? null) &&
    Boolean(a.isDeleted) === Boolean(b.isDeleted)
  )
}

export class SyncService {
  private intervalId: number | null = null
  private isSyncing = false
  /** Мьютекс пулла каталога: цикл синка и одобрение не гоняют курсор. */
  private catalogPull: Promise<void> | null = null
  private readonly baseUrl: string
  private bus: EventBus | null = null
  /** 65.14/9c: применить решение облака к локальному чеку. */
  private markingDecisionHandler:
    | ((decision: MarkingDecision) => Promise<MarkingDecisionApplyResult>)
    | null = null

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl ?? import.meta.env?.VITE_API_URL ?? 'https://api.pospoint.ru/api/v1'
  }

  /** Прокинуть шину событий (вызывает bootstrap) для эмита CATALOG_UPDATED. */
  public setEventBus(bus: EventBus): void {
    this.bus = bus
  }

  /** 65.14/9c: bootstrap подключает применение решений к sale/shift. */
  public setMarkingDecisionHandler(
    handler:
      | ((decision: MarkingDecision) => Promise<MarkingDecisionApplyResult>)
      | null
  ): void {
    this.markingDecisionHandler = handler
  }

  public start(intervalMs = 60000): void {
    if (this.intervalId) return
    // Мгновенный первый запуск при старте, если есть сеть.
    this.syncAll().catch((err) => console.error('[SyncService]', err))
    this.intervalId = window.setInterval(() => {
      this.syncAll().catch((err) => console.error('[SyncService]', err))
    }, intervalMs)
  }

  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  public async syncAll(): Promise<void> {
    if (!navigator.onLine || this.isSyncing) return
    this.isSyncing = true
    try {
      const token = await this.getMeta(META_DEVICE_TOKEN)
      if (!token) {
        // Касса ещё не авторизована директором в облаке.
        return
      }
      await this.pushReceipts(token)
      // Телефон узнаёт, что его offline-чек уже пробит на POS.
      await this.pullReceiptStatuses(token).catch((err) =>
        console.warn('[SyncService] receipt statuses pull failed:', err)
      )
      // 65.10: документы списания и складской ledger — после чеков.
      // Ошибка этих очередей не должна блокировать каталог/смены.
      await this.pushWriteoffs(token).catch((err) =>
        console.warn('[SyncService] writeoffs sync failed:', err)
      )
      await this.pushStockMovements(token).catch((err) =>
        console.warn('[SyncService] stock movements sync failed:', err)
      )
      // Авто-закрытие «забытой» смены старше 24 ч (таймаут) до отправки смен.
      await autoCloseStaleShift(db)
      await this.pushShifts(token)
      await this.pullShifts(token)
      // 65.14/9c: решения директора по hold-чекам (после реконсиляции смены).
      // Старый backend без 9c не должен останавливать остальной sync.
      await this.pullMarkingDecisions(token).catch((err) =>
        console.warn('[SyncService] marking decisions pull failed:', err)
      )
      await this.pushContainers(token)
      await this.pullContainers(token)
      // Заявки с касс: сначала выгружаем новые/изменённые, затем тянем каталог
      // и после него — статусы заявок (ARCH-TASK-58).
      await this.pushPendingProducts(token)
      // Отложенные правки цен — до пулла, чтобы дельта не вернула старое.
      await this.pushPendingEdits(token)
      await this.ensureCatalogResync(token)
      // Товары, созданные на кассе, в каталог НЕ выгружаются: они уходят в облако
      // только как «заявки» после одобрения директором (ARCH-TASK-58).
      await this.pullCatalog(token)
      await this.pullPendingStatuses(token)
      await this.pullCashiers(token)
      // Гигиена очереди: отправленные записи старше 30 дней не нужны
      // (отклонённые облаком остаются для разбора) — ARCH-TASK-65.9.1.1.
      await this.cleanupSyncedQueue()
    } catch (err) {
      console.warn('[SyncService] Sync cycle encountered error:', err)
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * PUSH: документы списания (65.10). Идемпотентность — writeoffId.
   */
  public async pushWriteoffs(token: string): Promise<void> {
    // 65.10: индекс syncState — не читаем весь ledger.
    const pending = await db.writeoffs.where('syncState').equals(0).toArray()
    if (pending.length === 0) return
    const batch = pending.slice(0, 200)
    const payload = await Promise.all(
      batch.map(async (w: Writeoff) => {
        // Старые записи до 9d не имеют origin. Восстанавливаем его по
        // связанному чеку, чтобы облако не получило «manual» после миграции.
        let origin = w.origin
        if (!origin && w.receiptId) {
          const receipt = await db.receipts
            .where('receiptId')
            .equals(w.receiptId)
            .first()
          if (
            receipt?.markingDecision === 'cancel' &&
            receipt.markingCancelMode === 'writeoff'
          ) {
            origin = 'marking_hold'
          } else if (receipt?.settlementType === 'income_return') {
            origin = 'return_writeoff'
          }
        }
        return {
          writeoff_id: w.writeoffId,
          receipt_id: w.receiptId && isValidUUID(w.receiptId) ? w.receiptId : undefined,
          reason_code: w.reasonCode,
          origin: origin ?? 'manual',
          comment: w.comment,
          items: w.items,
          total: w.total,
          user_id: w.userId,
          user_name: w.userName,
          zone_id: w.zoneId,
          created_at: new Date(w.createdAt).toISOString()
        }
      })
    )
    const response = await fetch(`${this.baseUrl}/sync/writeoffs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ writeoffs: payload })
    })
    if (!response.ok) {
      if (response.status === 422) {
        const body = (await response.json().catch(() => null)) as
          | { writeoff_id?: string; detail?: string; error?: string }
          | null
        const id = body?.writeoff_id
        if (id) {
          const rejected = batch.find((w) => w.writeoffId === id)
          if (rejected?.id != null) {
            await db.writeoffs.update(rejected.id, {
              syncState: 2,
              syncError: body?.detail ?? body?.error ?? 'rejected by cloud',
              syncRejectedAt: Date.now()
            })
          }
          console.warn(`[SyncService] writeoff ${id} отклонён облаком: ${body?.detail ?? ''}`)
          return
        }
      }
      throw new Error(`Failed to push writeoffs: HTTP ${response.status}`)
    }
    await db.transaction('rw', db.writeoffs, async () => {
      for (const w of batch) {
        if (w.id != null) await db.writeoffs.update(w.id, { synced: true, syncState: 1 })
      }
    })
  }

  /**
   * PUSH: складской ledger (65.10). Идемпотентность — movementId.
   */
  public async pushStockMovements(token: string): Promise<void> {
    const pending = await db.stock_movements.where('syncState').equals(0).toArray()
    if (pending.length === 0) return
    const batch = pending.slice(0, 500)
    const payload = batch.map((m: StockMovement) => ({
      movement_id: m.movementId,
      type: m.type,
      barcode: m.barcode,
      line_id: m.lineId,
      qty: m.qty,
      reason_code: m.reasonCode,
      comment: m.comment,
      receipt_id: m.receiptId && isValidUUID(m.receiptId) ? m.receiptId : undefined,
      writeoff_id: m.writeoffId,
      zone_id: m.zoneId,
      user_id: m.userId,
      created_at: new Date(m.createdAt).toISOString()
    }))
    const response = await fetch(`${this.baseUrl}/sync/stock-movements`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ movements: payload })
    })
    if (!response.ok) {
      if (response.status === 422) {
        const body = (await response.json().catch(() => null)) as
          | { movement_id?: string; detail?: string; error?: string }
          | null
        const id = body?.movement_id
        if (id) {
          const rejected = batch.find((m) => m.movementId === id)
          if (rejected?.id != null) {
            await db.stock_movements.update(rejected.id, {
              syncState: 2,
              syncError: body?.detail ?? body?.error ?? 'rejected by cloud',
              syncRejectedAt: Date.now()
            })
          }
          console.warn(`[SyncService] movement ${id} отклонён облаком: ${body?.detail ?? ''}`)
          return
        }
      }
      throw new Error(`Failed to push stock movements: HTTP ${response.status}`)
    }
    await db.transaction('rw', db.stock_movements, async () => {
      for (const m of batch) {
        if (m.id != null) {
          await db.stock_movements.update(m.id, { synced: true, syncState: 1 })
        }
      }
    })
  }

  /**
   * PUSH: отправка пачки неотправленных чеков из оффлайн-очереди.
   */
  public async pushReceipts(token: string): Promise<void> {
    const pendingSales = await db.sales_queue
      .filter((sale: QueuedSale) => !sale.is_synced)
      .toArray()

    if (pendingSales.length === 0) return

    const deviceId = await this.ensureDeviceId()

    // Нормализуем id к валидному UUIDv4. Старые чеки с префиксом rcpt-...
    // PostgreSQL отвергает (тип UUID) — переписываем их в sales_queue и receipts,
    // чтобы разблокировать очередь. Отслеживаем эффективный id для отметки synced.
    const effectiveIds: string[] = []
    const payload: SyncReceiptPayload[] = await Promise.all(
      pendingSales.map(async (s) => {
        let validId = s.id
        if (!isValidUUID(validId)) {
          validId = generateUUID()
          // Пере-ключуем запись в sales_queue (ключ id — первичный, менять через delete+put).
          await db.sales_queue.delete(s.id)
          await db.sales_queue.put({ ...s, id: validId })
          // Обновляем бизнес-id в самом чеке (receipts.receiptId).
          const receipt = await db.receipts.where('receiptId').equals(s.id).first()
          if (receipt?.id != null) {
            await db.receipts.update(receipt.id, { receiptId: validId })
          }
        }
        effectiveIds.push(validId)
        // Статус берём из актуального локального чека (мог измениться после
        // дофискализации), с фолбэком на снимок в очереди синка.
        const localReceipt = await db.receipts.where('receiptId').equals(validId).first()
        // Ссылка на исходный чек: легаси-id ('rcpt-…') в UUID-колонку облака
        // отправить нельзя — иначе 500 и застревание всей очереди (65.5).
        const originalReceiptId = localReceipt?.originalReceiptId ?? s.original_receipt_id
        const validOriginalId =
          originalReceiptId && isValidUUID(originalReceiptId) ? originalReceiptId : undefined
        return {
          id: validId,
          device_id: s.device_id || deviceId,
          total_amount: Number(s.total_amount),
          items: s.items,
          sold_at: s.sold_at || new Date().toISOString(),
          status: localReceipt?.status ?? s.status ?? 'fiscalized',
          // Контекст смены: зона/номер/кассир (для Z-отчёта по зоне в облаке).
          zone_id: localReceipt?.zoneId ?? s.zone_id,
          store_id: localReceipt?.storeId ?? s.store_id,
          shift_number: s.shift_number ?? localReceipt?.shiftNumber,
          cashier_name: localReceipt?.cashierName ?? s.cashier_name,
          // Тип документа: коррекции исключаются из выручки Z-отчёта в облаке.
          doc_type: localReceipt?.docType ?? s.doc_type ?? 'sale',
          // Способ оплаты — для разреза «наличные/карта/СБП» в «Обзоре».
          payment_method: localReceipt?.paymentMethod ?? s.payment_method,
          // Признак расчёта: возврат уходит с отрицательной суммой (ARCH-TASK-65).
          settlement_type: localReceipt?.settlementType ?? s.settlement_type ?? 'income',
          // Исходный чек возврата (ARCH-TASK-65.5) — связь для аудита в облаке.
          original_receipt_id: validOriginalId,
          offline_receipt_number: localReceipt?.offlineReceiptNumber ?? s.offline_receipt_number,
          // 65.10: признаки маркировки — для отчёта «Продажи без кода».
          marking_skipped: localReceipt?.markingSkipped ?? s.marking_skipped ?? false,
          marking_invalid: localReceipt?.markingInvalid ?? s.marking_invalid ?? false,
          marking_reason: localReceipt?.markingReason ?? s.marking_reason,
          cz_status: localReceipt?.czStatus,
          // 65.14: решение по hold-чеку (первое решение выигрывает в облаке).
          marking_decision: localReceipt?.markingDecision,
          marking_decision_source: localReceipt?.markingDecisionSource,
          marking_decided_by: localReceipt?.markingDecidedBy,
          marking_decided_at: toIso(localReceipt?.markingDecidedAt),
          marking_applied_at: toIso(localReceipt?.markingAppliedAt),
          marking_auto_at: toIso(localReceipt?.markingAutoFiscalizeAt),
          marking_comment: localReceipt?.markingComment,
          marking_print_state: localReceipt?.markingPrintState,
          marking_print_error:
            localReceipt?.markingPrintState === 'failed'
              ? localReceipt.markingComment
              : undefined
        }
      })
    )

    const response = await fetch(`${this.baseUrl}/sync/receipts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ receipts: payload })
    })

    if (!response.ok) {
      // 422 с id чека — облако отклонило конкретную запись (данные битые).
      // Исключаем только её, иначе одна битая запись блокирует всю очередь
      // продаж кассы (ARCH-TASK-65.9.1).
      if (response.status === 422) {
        const body = await response.json().catch(() => null)
        const rejection = parsePushRejection(response.status, body)
        if (rejection?.receiptId) {
          await this.markReceiptRejected(rejection.receiptId, rejection.reason)
          console.warn(
            `[SyncService] чек ${rejection.receiptId} отклонён облаком: ${rejection.reason ?? 'без причины'}`
          )
          return
        }
      }
      throw new Error(`Failed to push receipts: HTTP ${response.status}`)
    }

    const data = (await response.json()) as PushResponse
    if (data.success) {
      // Помечаем отправленные чеки (по эффективным id) как синхронизированные.
      await db.transaction('rw', db.sales_queue, async () => {
        for (const id of effectiveIds) {
          await db.sales_queue.update(id, { is_synced: true })
        }
      })
      debugLog(
        `[SyncService] Successfully synced ${data.processed} receipts (duplicates: ${data.duplicates})`
      )
    }
  }

  /**
   * PUSH: отправка кассовых смен в облако (мониторинг касс директором).
   * Шлём все локальные смены — сервер делает идемпотентный upsert по
   * (tenant_id, zone_id, shift_number): открытие вставляет, закрытие обновляет
   * статус/итоги/Z-отчёт. Вторая ОТКРЫТАЯ смена зоны отбивается частичным
   * уникальным индексом (сосед по зоне присоединяется, а не открывает свою).
   */
  /**
   * Пометить чек отклонённым облаком (422): повторно не отправляем, причину
   * сохраняем для UI и поддержки (ARCH-TASK-65.9.1).
   */
  /**
   * Отклонённые облаком чеки (422) — для экрана «Очередь синка» (65.9.1.1).
   * Новые сверху; `limit` защищает UI от гигантских историй.
   */
  public async listRejectedSales(limit = 50): Promise<QueuedSale[]> {
    const rows = await db.sales_queue.toArray()
    return rows
      .filter((s) => Boolean(s.sync_error))
      .sort((a, b) => (b.sync_rejected_at ?? 0) - (a.sync_rejected_at ?? 0))
      .slice(0, limit)
  }

  /**
   * Вернуть отклонённый чек в очередь синхронизации (после исправления
   * причины — например, обновления кассы или появления исходного чека).
   */
  public async retryRejectedSale(receiptId: string): Promise<void> {
    await db.transaction('rw', db.sales_queue, db.receipts, async () => {
      await db.sales_queue.update(receiptId, {
        is_synced: false,
        sync_error: undefined,
        sync_rejected_at: undefined
      })
      const receipt = await db.receipts.where('receiptId').equals(receiptId).first()
      if (receipt?.id != null) {
        await db.receipts.update(receipt.id, {
          syncError: undefined,
          syncRejectedAt: undefined
        })
      }
    })
  }

  /**
   * Чистим отправленные записи очереди старше `days` дней (65.9.1.1):
   * иначе sales_queue растёт бесконечно, а «Обзор» сканирует всю историю.
   * Отклонённые (sync_error) НЕ трогаем — их должен разобрать человек.
   */
  public async cleanupSyncedQueue(days = 30): Promise<void> {
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
    const stale = await db.sales_queue
      .filter((s) => s.is_synced && !s.sync_error)
      .toArray()
    const ids = stale
      .filter((s) => {
        const at = Date.parse(s.sold_at)
        return Number.isFinite(at) && at < cutoff
      })
      .map((s) => s.id)
    if (ids.length === 0) return
    await db.sales_queue.bulkDelete(ids)
    debugLog(`[SyncService] Очищено отправленных чеков из очереди: ${ids.length}`)
  }

  private async markReceiptRejected(receiptId: string, reason?: string): Promise<void> {
    const at = Date.now()
    const text = reason ?? 'rejected by cloud'
    await db.transaction('rw', db.sales_queue, db.receipts, async () => {
      await db.sales_queue.update(receiptId, {
        is_synced: true,
        sync_error: text,
        sync_rejected_at: at
      })
      const receipt = await db.receipts.where('receiptId').equals(receiptId).first()
      if (receipt?.id != null) {
        await db.receipts.update(receipt.id, { syncError: text, syncRejectedAt: at })
      }
    })
  }

  /** Забрать один чужой offline-чек зоны для печати на этой ККТ. */
  public async claimFiscalization(
    zoneId: string,
    deviceId: string
  ): Promise<RemoteFiscalizationItem | null> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return null
    const response = await fetch(`${this.baseUrl}/sync/fiscalization-queue/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ zone_id: zoneId, device_id: deviceId })
    })
    if (response.status === 204) return null
    if (!response.ok) {
      throw new Error(`Failed to claim fiscalization: HTTP ${response.status}`)
    }
    const data = (await response.json()) as { item?: FiscalizationQueueResponse }
    const item = data.item
    if (!item) return null
    return {
      queueId: Number(item.queue_id),
      receiptId: item.receipt_id,
      zoneId: item.zone_id ?? undefined,
      shiftNumber: item.shift_number ?? undefined,
      deviceId: item.device_id,
      attempts: Number(item.attempts) || 0,
      items: Array.isArray(item.items) ? item.items : [],
      totalAmount: Number(item.total_amount) || 0,
      paymentMethod: item.payment_method ?? undefined,
      docType: item.doc_type === 'correction' ? 'correction' : 'sale',
      settlementType:
        item.settlement_type === 'income_return' ||
        item.settlement_type === 'outcome' ||
        item.settlement_type === 'outcome_return'
          ? item.settlement_type
          : 'income',
      cashierName: item.cashier_name ?? undefined,
      originalReceiptId: item.original_receipt_id ?? undefined
    }
  }

  /** Вернуть результат печати чужого чека в облачную очередь. */
  public async completeFiscalization(
    queueId: number,
    deviceId: string,
    fiscalData: FiscalReceiptData
  ): Promise<void> {
    await this.sendFiscalizationResult(queueId, {
      device_id: deviceId,
      status: 'printed',
      fiscal_data: fiscalData
    })
  }

  /** Вернуть ошибку печати: очередь повторит чек (до 3 попыток). */
  public async failFiscalization(
    queueId: number,
    deviceId: string,
    error: string
  ): Promise<void> {
    await this.sendFiscalizationResult(queueId, {
      device_id: deviceId,
      status: 'failed',
      error
    })
  }

  private async sendFiscalizationResult(
    queueId: number,
    body: {
      device_id: string
      status: 'printed' | 'failed'
      fiscal_data?: FiscalReceiptData
      error?: string
    }
  ): Promise<void> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) throw new Error('device token is missing')
    const response = await fetch(
      `${this.baseUrl}/sync/fiscalization-queue/${queueId}/result`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      }
    )
    if (!response.ok) {
      throw new Error(`Failed to save fiscalization result: HTTP ${response.status}`)
    }
  }

  /** Сколько чеков зоны ждут дофискализации (для запрета Z). */
  public async getFiscalizationPendingCount(zoneId: string): Promise<number> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) throw new Error('device token is missing')
    const response = await fetch(
      `${this.baseUrl}/sync/fiscalization-queue/count?zone_id=${encodeURIComponent(zoneId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!response.ok) {
      throw new Error(`Failed to read fiscalization queue: HTTP ${response.status}`)
    }
    const data = (await response.json()) as { count?: number }
    return Number(data.count) || 0
  }

  /** Обновить локальные чеки после дофискализации на POS. */
  public async pullReceiptStatuses(token: string): Promise<void> {
    const deviceId = await this.ensureDeviceId()
    const response = await fetch(
      `${this.baseUrl}/sync/receipts/status?device_id=${encodeURIComponent(deviceId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!response.ok) return
    const data = (await response.json()) as { receipts?: RemoteReceiptStatus[] }
    const rows = Array.isArray(data.receipts) ? data.receipts : []
    if (rows.length === 0) return
    await db.transaction('rw', db.receipts, async () => {
      for (const row of rows) {
        const local = await db.receipts.where('receiptId').equals(row.id).first()
        if (!local || local.id == null || row.status !== 'fiscalized') continue
        if (!row.fiscal_data) continue
        await db.receipts.update(local.id, {
          status: 'fiscalized',
          fiscalData: row.fiscal_data as FiscalReceiptData,
          offlineReceiptNumber: row.offline_receipt_number ?? local.offlineReceiptNumber
        })
      }
    })
  }


  public async openShiftOnServer(zoneId: string, deviceId: string, cashierName: string, fiscalizationEnabled = true): Promise<OpenShiftResult | null> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (token == null) throw new Error('device token is missing')
    const response = await fetch(this.baseUrl + '/sync/shifts/open', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }, body: JSON.stringify({ zone_id: zoneId, device_id: deviceId, cashier_name: cashierName, fiscalization_enabled: fiscalizationEnabled }) })
    if (response.ok === false) throw new Error('Failed to open shift: HTTP ' + response.status)
    const data = await response.json() as { shift?: OpenShiftResult }
    return data.shift ?? null
  }

  public async pushShifts(token: string): Promise<void> {
    const shifts = await db.shifts.toArray()
    if (shifts.length === 0) return

    const deviceId = await this.ensureDeviceId()
    const zoneId = await this.ensureZoneId()

    const payload = shifts.map((s: PosShift) => ({
      device_id: deviceId,
      zone_id: s.zoneId ?? zoneId,
      store_id: s.storeId,
      opened_by_device: s.openedByDevice ?? deviceId,
      shift_number: s.shiftNumber,
      cashier_name: s.cashierName,
      status: s.status,
      fiscalization_enabled: Boolean(s.fiscalizationEnabled),
      initial_cash: Number(s.initialCash) || 0,
      total_sales: Number(s.totalSales) || 0,
      sales_count: Number(s.salesCount) || 0,
      z_report_data: s.zReportData ? safeParseJson(s.zReportData) : null,
      opened_at: new Date(s.openedAt).toISOString(),
      closed_at: s.closedAt ? new Date(s.closedAt).toISOString() : null
    }))

    const res = await fetch(`${this.baseUrl}/sync/shifts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ shifts: payload })
    })
    if (!res.ok) {
      throw new Error(`Failed to push shifts: HTTP ${res.status}`)
    }
    debugLog(`[SyncService] Pushed ${payload.length} shifts to cloud`)
  }

  /**
   * PULL: реконсиляция смен по ЗОНЕ (обратная синхронизация).
   *
   * Смена принадлежит зоне, а не устройству. Поэтому:
   *  - если в облаке открыта смена МОЕЙ ЗОНЫ, а локально открытой нет —
   *    ПРИСОЕДИНЯЕМСЯ: принимаем смену локально и делаем её активной;
   *  - если смена МОЕЙ ЗОНЫ закрыта в облаке (устройством-открывателем или
   *    директором из админки) — закрываем её локально и снимаем activeShiftId.
   * Защита от «фантомно открытых» смен и от расхождения статусов по зоне.
   */
  public async pullShifts(token: string): Promise<void> {
    const zoneId = await this.ensureZoneId()
    const response = await fetch(
      `${this.baseUrl}/sync/shifts?zone=${encodeURIComponent(zoneId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    if (!response.ok) {
      throw new Error(`Failed to pull shifts: HTTP ${response.status}`)
    }

    const data = (await response.json()) as { shifts: ShiftRowDto[] }
    const remote = Array.isArray(data.shifts) ? data.shifts : []
    if (remote.length === 0) return

    const localShifts = await db.shifts.toArray()
    const activeShiftIdRaw = await this.getMeta(META_ACTIVE_SHIFT_ID)
    const activeShiftId = activeShiftIdRaw ? Number(activeShiftIdRaw) : null

    let reconciled = 0
    let adopted = 0

    await db.transaction('rw', db.shifts, db.meta, async () => {
      // 1. Закрытия: облако закрыло смену зоны — гасим локальную копию.
      for (const r of remote.filter((s) => s.status === 'closed')) {
        const local = localShifts.find((s) => s.shiftNumber === r.shift_number)
        if (!local || local.id == null || local.status === 'closed') continue
        const parsed = r.closed_at ? Date.parse(r.closed_at) : NaN
        await db.shifts.update(local.id, {
          status: 'closed',
          closedAt: Number.isFinite(parsed) ? parsed : Date.now(),
          zReportData:
            r.z_report_data != null ? JSON.stringify(r.z_report_data) : local.zReportData
        })
        local.status = 'closed'
        if (activeShiftId === local.id) {
          await db.meta.delete(META_ACTIVE_SHIFT_ID)
        }
        reconciled += 1
      }

      // 2. Присоединение: в зоне открыта смена, а локально активной нет.
      if (localShifts.some((s) => s.status === 'open')) return
      const remoteOpen = remote
        .filter((s) => s.status === 'open')
        .sort((a, b) => Date.parse(a.opened_at) - Date.parse(b.opened_at))[0]
      if (!remoteOpen) return
      // Не воскрешаем номер, который локально уже закрыт (анти-зомби).
      if (localShifts.some((s) => s.shiftNumber === remoteOpen.shift_number)) return

      const openedAt = Date.parse(remoteOpen.opened_at)
      const newId = (await db.shifts.add({
        shiftId: remoteOpen.id,
        shiftNumber: remoteOpen.shift_number,
        cashierId: '',
        cashierName: remoteOpen.cashier_name,
        openedAt: Number.isFinite(openedAt) ? openedAt : Date.now(),
        status: 'open',
        fiscalizationEnabled: Boolean(remoteOpen.fiscalization_enabled),
        initialCash: Number(remoteOpen.initial_cash) || 0,
        totalSales: Number(remoteOpen.total_sales) || 0,
        totalCash: 0,
        totalCard: 0,
        totalSbp: 0,
        salesCount: Number(remoteOpen.sales_count) || 0,
        zoneId: remoteOpen.zone_id ?? zoneId,
        storeId: remoteOpen.store_id ?? undefined,
        openedByDevice: remoteOpen.opened_by_device ?? remoteOpen.device_id
      })) as number
      await db.meta.put({ key: META_ACTIVE_SHIFT_ID, value: newId })
      adopted += 1
    })

    if (reconciled > 0 || adopted > 0) {
      debugLog(
        `[SyncService] Shifts reconciled for zone ${zoneId}: closed=${reconciled}, joined=${adopted}`
      )
      // Смена зоны изменилась извне (присоединение/закрытие) — касса обновляет UI.
      this.bus?.emit(PosEvent.SHIFTS_UPDATED, {})
    }
  }

  /**
   * 65.14/9c: PULL решений директора по hold-чекам и их применение на кассе.
   *
   * Решение применяется только через handler из bootstrap (sale + shift).
   * После применения/ошибки отправляем ack, чтобы облако не отдавало его
   * повторно бесконечно.
   */
  public async pullMarkingDecisions(token: string): Promise<void> {
    const deviceId = await this.ensureDeviceId()
    const response = await fetch(
      `${this.baseUrl}/sync/marking-decisions?device_id=${encodeURIComponent(deviceId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!response.ok) {
      throw new Error(`Failed to pull marking decisions: HTTP ${response.status}`)
    }
    const data = (await response.json()) as { decisions?: MarkingDecisionDto[] }
    const decisions = Array.isArray(data.decisions) ? data.decisions : []
    if (decisions.length === 0) return
    if (!this.markingDecisionHandler) {
      console.warn('[SyncService] marking decisions: handler не подключён')
      return
    }

    for (const raw of decisions) {
      const decision: MarkingDecision = {
        receiptId: raw.receipt_id,
        decision: raw.marking_decision,
        cancelMode: raw.marking_cancel_mode ?? undefined,
        comment: raw.marking_comment ?? undefined,
        decidedBy: raw.marking_decided_by ?? undefined,
        decidedAt: raw.marking_decided_at ?? undefined,
        status: raw.status,
        totalAmount: Number(raw.total_amount),
        applyAttempts: raw.marking_apply_attempts
      }
      let result: MarkingDecisionApplyResult
      try {
        result = await this.markingDecisionHandler(decision)
      } catch (error) {
        result = {
          ok: false,
          reason: error instanceof Error ? error.message : String(error)
        }
      }
      try {
        await this.ackMarkingDecision(
          token,
          decision.receiptId,
          deviceId,
          result.ok ? 'applied' : 'failed',
          result.ok ? undefined : result.reason
        )
      } catch (error) {
        console.warn('[SyncService] marking decision ack failed:', error)
      }
    }
  }

  /** 65.14/9c: подтвердить облаку применение/ошибку решения. */
  public async ackMarkingDecision(
    token: string,
    receiptId: string,
    deviceId: string,
    result: 'applied' | 'failed',
    error?: string
  ): Promise<void> {
    const response = await fetch(`${this.baseUrl}/sync/marking-decisions/ack`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        receipt_id: receiptId,
        device_id: deviceId,
        result,
        error
      })
    })
    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(`Failed to ack marking decision: HTTP ${response.status} ${body}`)
    }
  }

  /**
   * Запросить ПОЛНЫЙ Z-отчёт смены по зоне из облака (агрегация по всем
   * устройствам рабочего места). Используется при онлайн-закрытии смены.
   * @returns агрегат или null (офлайн/ошибка/нет токена).
   */
  public async fetchShiftZ(zoneId: string, shiftNumber: number): Promise<ShiftZReport | null> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return null
    try {
      const url = `${this.baseUrl}/sync/shift-z?zone=${encodeURIComponent(zoneId)}&shift_number=${shiftNumber}`
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) {
        console.warn(`[SyncService] fetchShiftZ: HTTP ${response.status}`)
        return null
      }
      return (await response.json()) as ShiftZReport
    } catch (err) {
      console.warn('[SyncService] fetchShiftZ failed:', err)
      return null
    }
  }

  /**
   * PUSH: выгрузка локального пула вскрытых тар в облако (контроль директора).
   * Идемпотентный upsert на сервере по (tenant_id, device_id, datamatrix):
   * повторная отправка лишь обновляет остаток и статус.
   */
  public async pushContainers(token: string): Promise<void> {
    const containers = await db.opened_containers.toArray()
    if (containers.length === 0) return

    const deviceId = await this.ensureDeviceId()
    const zoneId = await this.ensureZoneId()
    const payload: ContainerPushPayload[] = containers.map((c) => ({
      device_id: deviceId,
      // Запись пула принадлежит зоне: у тары своя зона, иначе — зона устройства.
      zone_id: c.zoneId ?? zoneId,
      barcode: c.barcode,
      name: c.name,
      datamatrix: c.datamatrix,
      initial_weight: Number(c.initialWeight),
      remaining_weight: Number(c.remainingWeight),
      status: c.status,
      opened_at: new Date(c.openedAt).toISOString()
    }))

    const res = await fetch(`${this.baseUrl}/sync/containers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ containers: payload })
    })
    if (!res.ok) {
      throw new Error(`Failed to push containers: HTTP ${res.status}`)
    }
    debugLog(`[SyncService] Pushed ${payload.length} opened containers to cloud`)
  }

  /**
   * PULL: подтянуть общий пул вскрытых тар СВОЕЙ ЗОНЫ из облака.
   *
   * Зона объединяет устройства рабочего места: тару, вскрытую на POS-терминале,
   * видит телефон той же зоны (и наоборот). Сопоставляем по datamatrix:
   * существующую запись обновляем (остаток/статус/название), новую — добавляем.
   */
  public async pullContainers(token: string): Promise<void> {
    const zoneId = await this.ensureZoneId()
    const response = await fetch(
      `${this.baseUrl}/sync/containers?zone=${encodeURIComponent(zoneId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    if (!response.ok) {
      throw new Error(`Failed to pull containers: HTTP ${response.status}`)
    }

    const data = (await response.json()) as { containers: ContainerDto[] }
    const remote = Array.isArray(data.containers) ? data.containers : []
    if (remote.length === 0) return

    await db.transaction('rw', db.opened_containers, async () => {
      // Пул тары мал (десятки записей) — сопоставляем по datamatrix в памяти
      // (в схеме v7 datamatrix не индексирован).
      const local = await db.opened_containers.toArray()
      for (const c of remote) {
        const openedAt = Date.parse(c.opened_at)
        const existing = local.find((x) => x.datamatrix === c.datamatrix)
        if (existing?.id != null) {
          await db.opened_containers.update(existing.id, {
            name: c.name,
            remainingWeight: Number(c.remaining_weight),
            status: c.status,
            zoneId: c.zone_id ?? zoneId
          })
          continue
        }
        const newId = await db.opened_containers.add({
          zoneId: c.zone_id ?? zoneId,
          barcode: c.barcode,
          name: c.name,
          datamatrix: c.datamatrix,
          initialWeight: Number(c.initial_weight),
          remainingWeight: Number(c.remaining_weight),
          status: c.status,
          openedAt: Number.isFinite(openedAt) ? openedAt : Date.now()
        })
        // Чтобы не задвоить запись внутри одной пачки.
        local.push({
          id: newId as number,
          zoneId: c.zone_id ?? zoneId,
          barcode: c.barcode,
          name: c.name,
          datamatrix: c.datamatrix,
          initialWeight: Number(c.initial_weight),
          remainingWeight: Number(c.remaining_weight),
          status: c.status,
          openedAt: Number.isFinite(openedAt) ? openedAt : Date.now()
        })
      }
    })

    // Касса перечитывает список тар и бейдж без перезагрузки страницы.
    this.bus?.emit(PosEvent.CONTAINERS_UPDATED, {})
    debugLog(`[SyncService] Pulled ${remote.length} containers for zone ${zoneId}`)
  }

  /**
   * Pазовый полный пересинк каталога после исправления матчинга удалённых строк
   * (ARCH-TASK-61.0c): без него ошибочно удалённые товары не вернутся, т.к. их
   * `updated_at` старше курсора устройства.
   */
  private async ensureCatalogResync(token: string): Promise<void> {
    const marker = await this.getMeta(META_CATALOG_RESYNC)
    if (marker === CATALOG_RESYNC_MARKER) return
    // Если пулл уже идёт (например, кнопка «Одобрить»), дожидаемся его: иначе
    // вызов присоединится к промису со старым курсором, маркер запишется, а
    // каталог целиком так и не будет прочитан.
    if (this.catalogPull) await this.catalogPull
    await this.setMeta(META_CATALOG_SYNCED_AT, '1970-01-01T00:00:00.000Z')
    await this.pullCatalog(token)
    await this.setMeta(META_CATALOG_RESYNC, CATALOG_RESYNC_MARKER)
    debugLog('[SyncService] Catalog resynced (delete-matching fix)')
  }

  /** Принудительный полный пересинк каталога (кнопка в «Диагностике»). */
  /**
   * Подтянуть загруженную картинку категории и закэшировать в Dexie.
   * Возвращает true, если картинка получена.
   */
  public async fetchCategoryImage(name: string): Promise<boolean> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return false
    const response = await fetch(
      `${this.baseUrl}/catalog/categories/${encodeURIComponent(name)}/image`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!response.ok) return false
    const blob = await response.blob()
    const mime = response.headers.get('content-type') ?? blob.type ?? 'image/png'
    const key = `category:${name}`
    const existing = await db.assets.where('key').equals(key).first()
    const record = {
      key,
      ownerType: 'category' as const,
      ownerKey: name,
      mime,
      blob,
      updatedAt: Date.now()
    }
    if (existing?.id != null) {
      await db.assets.update(existing.id, record)
    } else {
      await db.assets.add(record)
    }
    return true
  }

  /**
   * Ролевая раскладка админки из облака. `null` — офлайн, нет токена
   * или облако ещё не знает раскладки: работаем на локальном кэше.
   */
  public async fetchRoleNavigation(): Promise<unknown | null> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return null
    try {
      const response = await fetch(`${this.baseUrl}/sync/role-navigation`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) return null
      const data = (await response.json()) as { config?: unknown }
      return data?.config ?? null
    } catch {
      return null
    }
  }

  /** Отправить раскладку в облако (last-write-wins по updatedAt на сервере). */
  public async pushRoleNavigation(config: unknown): Promise<boolean> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return false
    try {
      const response = await fetch(`${this.baseUrl}/sync/role-navigation`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ config })
      })
      return response.ok
    } catch {
      return false
    }
  }

  public async resyncCatalog(token: string): Promise<void> {
    if (this.catalogPull) await this.catalogPull
    await this.setMeta(META_CATALOG_SYNCED_AT, '1970-01-01T00:00:00.000Z')
    await this.pullCatalog(token)
    await this.setMeta(META_CATALOG_RESYNC, CATALOG_RESYNC_MARKER)
  }

  /**
   * PULL: time-based дельта каталога (ARCH-TASK-59).
   *
   * Курсор — составной (updated_at, id) последней полученной строки в
   * микросекундной точности (сервер отдаёт updated_at_cursor строкой): с
   * миллисекундным курсором последняя строка переотдавалась бы вечно.
   *
   * Запрос стартует на CATALOG_LOOKBACK_MS раньше курсора — страховка от гонки
   * watermark-а (строка закоммичена после чтения). Повторно пришедшие неизменённые
   * строки отбрасываются сравнением с локальной копией, поэтому перекрытие не
   * создаёт ни лишних записей, ни лишних событий.
   */
  public async pullCatalog(token: string): Promise<void> {
    // Одновременные вызовы (цикл синка + одобрение заявки из админки) не должны
    // гонять курсор: второй вызов дожидается результата первого.
    if (this.catalogPull) return this.catalogPull
    this.catalogPull = this.pullCatalogOnce(token).finally(() => {
      this.catalogPull = null
    })
    return this.catalogPull
  }

  /** Upsert дерева категорий из облака (внутри транзакции или своей). */
  private async upsertCategories(
    rows: CatalogCategoryDto[],
    now: number
  ): Promise<void> {
    for (const row of rows) {
      const existing = await db.categories.where('name').equals(row.name).first()
      const record: Category = {
        name: row.name,
        parentName: row.parent_name ?? null,
        department: row.department ?? undefined,
        isActive: row.is_active !== false,
        sortOrder: row.sort_order ?? undefined,
        imageUrl: row.image_url ?? undefined,
        hasImage: row.has_image === true,
        updatedAt: now
      }
      if (existing?.id != null) {
        await db.categories.update(existing.id, record)
      } else {
        await db.categories.add(record)
      }
    }
  }

  private async pullCatalogOnce(token: string): Promise<void> {
    const sinceRaw = await this.getMeta(META_CATALOG_SYNCED_AT)
    const storedSince = sinceRaw && sinceRaw.length > 0 ? sinceRaw : null
    // Первый запрос — с перекрытием окна; после первой страницы курсор берём
    // из ответа сервера без сдвига (страница уже внутри окна).
    let cursorSince = storedSince
      ? shiftIso(storedSince, -CATALOG_LOOKBACK_MS)
      : '1970-01-01T00:00:00.000Z'
    let cursorId = 0

    const batches: CatalogProductDto[] = []
    let categories: CatalogCategoryDto[] = []
    let latestVersion = 0

    // До 5 страниц за цикл: типичный магазин (~1 000 SKU) получает каталог
    // целиком, очень крупный — порциями, без потери строк.
    for (let page = 0; page < 5; page++) {
      const response = await fetch(
        `${this.baseUrl}/sync/catalog?since=${encodeURIComponent(cursorSince)}&after_id=${cursorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (!response.ok) {
        throw new Error(`Failed to pull catalog: HTTP ${response.status}`)
      }
      const data = (await response.json()) as CatalogResponse
      if (data.latest_version > 0) latestVersion = data.latest_version
      if (categories.length === 0 && Array.isArray(data.categories)) {
        categories = data.categories
      }

      const rows = Array.isArray(data.products) ? data.products : []
      if (data.up_to_date || rows.length === 0) break

      batches.push(...rows)
      if (!data.cursor) break
      cursorSince = data.cursor.since
      cursorId = data.cursor.id
      if (rows.length < 5000) break
    }

    if (batches.length === 0) {
      if (categories.length > 0) {
        await db.transaction('rw', db.categories, async () => {
          await this.upsertCategories(categories, Date.now())
        })
      }
      if (latestVersion > 0) {
        await this.setMeta(META_CATALOG_VERSION, String(latestVersion))
      }
      return // Изменений нет — каталог актуален.
    }

    const now = Date.now()
    let applied = 0

    await db.transaction(
      'rw',
      [db.products, db.catalogVersions, db.meta, db.categories],
      async () => {
      // Цены, изменённые в админке и ещё не отправленные в облако, приоритетнее:
      // дельта не должна откатывать правку директора (ARCH-TASK-60.3).
      // Читаем внутри транзакции — иначе правка, сделанная во время пулла,
      // откатилась бы на один цикл.
      const pendingEdits = await pendingEditMap()
      // 1. Запись облачной версии (для раздела «Версии каталога»).
      if (latestVersion > 0) {
        const existingVer = await db.catalogVersions.get(latestVersion)
        if (existingVer && !existingVer.source) {
          // Бэкфилл для версий, полученных до появления журнала изменений.
          await db.catalogVersions.update(latestVersion, { source: 'cloud' })
        }
        if (!existingVer) {
          await db.catalogVersions.put({
            id: latestVersion,
            versionName: `Версия ${latestVersion} (Облако)`,
            note: 'Автосинхронизация из облака',
            source: 'cloud',
            createdAt: now
          })
        }
      }

      // 2. Товары. Матчинг по стабильным ключам: client_uuid (заявка с кассы) →
      //    cloudId (id в облаке) → (версия + НЕПУСТОЙ ШК). Индекс
      //    [catalogVersionId+barcode] не уникален: у товаров без ШК ключ
      //    [version, ''] общий, поэтому по пустому ШК не ищем.
      for (const p of batches) {
        const unit = p.unit || 'шт'
        // Версия строки из облака: публикация перепривязывает все товары и
        // трогает updated_at, поэтому дельта приносит их уже в новой версии.
        const targetVersion = p.catalog_version_id ?? latestVersion
        const existing =
          (p.client_uuid
            ? await db.products.where('clientUuid').equals(p.client_uuid).first()
            : undefined) ??
          (await db.products.where('cloudId').equals(p.id).first()) ??
          (p.barcode
            ? await db.products
                .where('[catalogVersionId+barcode]')
                .equals([targetVersion, p.barcode])
                .first()
            : await db.products
                .where('catalogVersionId')
                .equals(targetVersion)
                // Разовое сопоставление строк, пришедших до появления cloudId
                // (ARCH-TASK-58.1): товар без ШК ищем по имени, чтобы не
                // наплодить дублей при первом time-based пулле.
                .and((x) => x.cloudId == null && x.name === p.name)
                .first())

        // Совпадение по стабильному ключу — это та же облачная запись. Совпадение
        // по ШК/имени — лишь «похожая» локальная строка: удаление из облака на неё
        // распространять нельзя, иначе касса теряет живые товары (ARCH-TASK-61.0c).
        const matchedByCloudId =
          (p.client_uuid != null && existing?.clientUuid === p.client_uuid) ||
          (existing?.cloudId != null && existing.cloudId === p.id)
        if (p.is_deleted && !matchedByCloudId) {
          continue
        }

        // Неотправленные правки админки приоритетнее облачных значений.
        // `minStock = null` — осознанный сброс, поэтому ?? тут не подходит.
        const pending = pendingEdits.get(p.id)
        const product: Product = {
          catalogVersionId: targetVersion,
          cloudId: p.id,
          barcode: p.barcode,
          name: p.name,
          price: pending?.price ?? Number(p.price),
          minStock:
            pending?.minStock !== undefined
              ? (pending.minStock ?? undefined)
              : p.min_stock == null
                ? undefined
                : Number(p.min_stock),
          stock: Number(p.stock),
          unit,
          category: p.category || 'Общее',
          categoryParent: p.category_parent?.trim() || p.category || 'Общее',
          categoryOrdinal: p.category_ordinal ?? undefined,
          isActive: p.is_active !== false,
          sourceMeta: p.source_meta ?? undefined,
          subcategory: p.subcategory?.trim() || undefined,
          isWeight: p.is_weight ?? unit === 'кг',
          requiresMarking: Boolean(p.requires_marking),
          // «Создан на кассе» живёт только там, где товар реально завели:
          // иначе одобренные товары висели бы в модерации на всех терминалах.
          isCreatedOnPos: Boolean(existing?.isCreatedOnPos),
          // Клиентские поля заявки переживают пулл (нужны идемпотентному пушу).
          clientUuid: existing?.clientUuid ?? p.client_uuid ?? undefined,
          cloudSyncedAt: existing?.cloudSyncedAt ?? (p.is_created_on_pos ? now : undefined),
          cloudStatus: existing?.cloudStatus ?? (p.is_created_on_pos ? 'approved' : undefined),
          // Для облачной строки состояние удаления берём из облака, а не
          // «или» с локальным: иначе ошибочно удалённые товары не оживают.
          isDeleted: matchedByCloudId
            ? Boolean(p.is_deleted)
            : Boolean(p.is_deleted) || Boolean(existing?.isDeleted),
          updatedAt: now
        }

        if (existing?.id != null) {
          // Перекрытие окна приносит те же строки повторно — не пишем и не
          // считаем их изменениями (иначе лишние события каталога каждый цикл).
          if (isSameProduct(existing, product)) continue
          // put вместо update: sourceMeta — вложенный объект с index signature,
          // который Dexie UpdateSpec не принимает.
          await db.products.put({ ...existing, ...product, id: existing.id })
          applied++
        } else {
          await db.products.add(product)
          applied++
        }
      }

      // 3. Дерево категорий — tenant-level, не входит в версию каталога.
      if (categories.length > 0) {
        await this.upsertCategories(categories, now)
      }

      // 4. Мета: активная версия, номер версии и курсор — атомарно с товарами.
      if (latestVersion > 0) {
        await db.meta.put({ key: META_ACTIVE_VERSION, value: latestVersion })
        await db.meta.put({ key: META_CATALOG_VERSION, value: String(latestVersion) })
      }
      await db.meta.put({ key: META_CATALOG_SYNCED_AT, value: cursorSince })
      }
    )

    // 4. Касса мгновенно обновит плитки без F5 — только если что-то изменилось.
    if (applied > 0) {
      this.bus?.emit(PosEvent.CATALOG_UPDATED, {
        versionId: latestVersion,
        count: applied
      })
    }

    debugLog(
      `[SyncService] Catalog delta: ${batches.length} received, ${applied} applied, version ${latestVersion}`
    )
  }

  /**
   * PULL: обновление локального справочника кассиров для оффлайн-PIN.
   */
  public async pullCashiers(token: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/pos/pins`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    if (!response.ok) return

    const cashiers = (await response.json()) as CashierDto[]
    if (Array.isArray(cashiers)) {
      const before = JSON.stringify(await db.cashiers.toArray())
      await db.transaction('rw', db.cashiers, async () => {
        await db.cashiers.clear()
        for (const c of cashiers) {
          await db.cashiers.put({
            user_id: c.user_id,
            name: c.name,
            pin_hash: c.pin_hash,
            permissions: c.permissions,
            role: c.role
          })
        }
      })
      const after = JSON.stringify(await db.cashiers.toArray())
      if (before !== after) {
        // Права могли измениться в облаке — активная сессия обновится без входа.
        this.bus?.emit(PosEvent.CASHIERS_UPDATED, {})
      }
    }
  }

  /**
   * PUSH: заявки с касс — товары, созданные «на лету» и ещё не отправленные.
   * Сервер обновляет только pending; уже рассмотренные вернёт в skipped.
   */
  public async pushPendingProducts(token: string): Promise<void> {
    const pending = await db.products
      .filter(
        (p) =>
          Boolean(p.isCreatedOnPos) &&
          !p.isDeleted &&
          !p.cloudSyncedAt &&
          Boolean(p.clientUuid)
      )
      .toArray()
    if (pending.length === 0) return

    const deviceId = await this.ensureDeviceId()
    const shiftIdRaw = await this.getMeta(META_ACTIVE_SHIFT_ID)
    const shift = shiftIdRaw ? await db.shifts.get(Number(shiftIdRaw)) : undefined

    const items = pending.map((p) => ({
      client_uuid: p.clientUuid as string,
      device_id: deviceId,
      created_by_user_id: shift?.cashierId ?? null,
      created_by_name: p.createdByName ?? shift?.cashierName ?? null,
      name: p.name,
      barcode: p.barcode,
      price: p.price,
      category: p.category || 'Общее',
      unit: p.unit || 'шт',
      is_weight: Boolean(p.isWeight),
      requires_marking: Boolean(p.requiresMarking),
      client_created_at: new Date(p.updatedAt).toISOString()
    }))

    const response = await fetch(`${this.baseUrl}/sync/pending-products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ items })
    })
    if (!response.ok) {
      console.warn(`[SyncService] pushPendingProducts: HTTP ${response.status}`)
      return
    }
    const data = (await response.json()) as {
      accepted?: string[]
      skipped?: Array<{ client_uuid: string; reason: string }>
    }
    const accepted = new Set(data.accepted ?? [])
    const skipped = new Set((data.skipped ?? []).map((s) => s.client_uuid))

    for (const p of pending) {
      if (p.id == null || !p.clientUuid) continue
      if (accepted.has(p.clientUuid)) {
        await db.products.update(p.id, { cloudSyncedAt: Date.now(), cloudStatus: 'pending' })
      } else if (skipped.has(p.clientUuid)) {
        // Заявка уже рассмотрена в облаке: фиксируем факт отправки, но статус
        // не перезаписываем — точный придёт в pullPendingStatuses.
        await db.products.update(p.id, { cloudSyncedAt: Date.now() })
      }
    }
    if (accepted.size > 0 || skipped.size > 0) {
      // Экран «Заявки с касс» перечитывает статусы без F5.
      this.bus?.emit(PosEvent.PENDING_UPDATED, {})
    }
    debugLog(`[SyncService] Pushed ${accepted.size} pending products`)
  }

  /**
   * PUSH: отложенные правки товаров из админки (ARCH-TASK-60.3, 64a).
   *
   * Отправляем ровно те поля, что есть в записи (цена и/или минимальный
   * остаток). Успех или постоянная ошибка убирают позицию из очереди,
   * остальные повторятся на следующем цикле; локальные значения сохраняются.
   */
  public async pushPendingEdits(token: string): Promise<void> {
    const pending = await listPendingEdits()
    if (pending.length === 0) return

    const done: SentEdit[] = []
    for (const item of pending) {
      const sent = { cloudId: item.cloudId } as SentEdit
      for (const field of EDITABLE_FIELDS) {
        const value = item[field]
        if (value !== undefined) {
          ;(sent as unknown as Record<string, unknown>)[field] = value
        }
      }
      try {
        const response = await fetch(`${this.baseUrl}/catalog/products/patch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            product_id: item.cloudId,
            price: item.price,
            min_stock: item.minStock,
            name: item.name,
            barcode: item.barcode,
            category: item.category,
            category_parent: item.categoryParent,
            requires_marking: item.requiresMarking
          })
        })
        // Успех или постоянная ошибка (битые данные, нет прав, товара нет) —
        // держать такую позицию в очереди бессмысленно.
        if (
          response.ok ||
          response.status === 400 ||
          response.status === 403 ||
          response.status === 404
        ) {
          done.push(sent)
        } else if (response.status !== 401) {
          // 401 — проблема сессии: повторяем без списания попытки.
          const dropped = await failPendingEdit(sent)
          if (dropped) {
            console.warn(
              `[SyncService] Pending product edit dropped after retries: product ${item.cloudId}`
            )
          }
        }
      } catch {
        // Сеть недоступна — считаем попытку, позиция остаётся в очереди.
        await failPendingEdit(sent)
      }
    }
    if (done.length > 0) {
      await removePendingEdits(done)
      debugLog(`[SyncService] Pushed ${done.length} pending product edit(s)`)
    }
  }

  /**
   * PULL: статусы заявок этого устройства. approved — товар остаётся локально,
   * rejected — мягко удаляется на терминале-создателе.
   */
  public async pullPendingStatuses(token: string): Promise<void> {
    const deviceId = await this.ensureDeviceId()
    // Точная реконсиляция по client_uuid: не зависит от device_id, который
    // меняется при переустановке кассы. Нет своих заявок — спросим по устройству.
    // Только нерешённые заявки: одобренные/отклонённые реконсилировать незачем.
    const localUuids = (
      await db.products
        .filter(
          (p) =>
            Boolean(p.isCreatedOnPos) &&
            Boolean(p.clientUuid) &&
            p.cloudStatus !== 'approved' &&
            p.cloudStatus !== 'rejected'
        )
        .toArray()
    )
      .map((p) => p.clientUuid as string)
      .slice(0, 100)
    const selection =
      localUuids.length > 0
        ? `client_uuids=${encodeURIComponent(localUuids.join(','))}`
        : `device_id=${encodeURIComponent(deviceId)}`
    const response = await fetch(`${this.baseUrl}/sync/pending-products?${selection}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) return
    const data = (await response.json()) as {
      items?: Array<{ client_uuid: string; status: 'pending' | 'approved' | 'rejected' }>
    }
    const items = data.items ?? []
    if (items.length === 0) return

    let changed = false
    for (const item of items) {
      const product = await db.products.filter((p) => p.clientUuid === item.client_uuid).first()
      if (!product?.id) continue
      if (item.status === 'rejected') {
        await db.products.update(product.id, {
          cloudStatus: 'rejected',
          isDeleted: true,
          updatedAt: Date.now()
        })
        changed = true
      } else if (item.status === 'approved') {
        await db.products.update(product.id, {
          cloudStatus: 'approved',
          cloudSyncedAt: product.cloudSyncedAt || Date.now()
        })
        changed = true
      } else if (product.cloudStatus !== 'pending') {
        await db.products.update(product.id, { cloudStatus: 'pending' })
        changed = true
      }
    }
    if (changed) {
      const versionRaw = await this.getMeta(META_CATALOG_VERSION)
      this.bus?.emit(PosEvent.CATALOG_UPDATED, {
        versionId: Number(versionRaw ?? 0),
        count: 0
      })
      this.bus?.emit(PosEvent.PENDING_UPDATED, {})
    }
  }

  /** Отмена заявки в облаке (best-effort, ошибки не пробрасываем). */
  public async cancelPending(clientUuid: string): Promise<void> {
    const token = await this.getMeta(META_DEVICE_TOKEN)
    if (!token) return
    try {
      const deviceId = await this.ensureDeviceId()
      await fetch(
        `${this.baseUrl}/sync/pending-products/${encodeURIComponent(clientUuid)}?device_id=${encodeURIComponent(deviceId)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      )
    } catch {
      /* сеть недоступна — повторим при удалении на сервере/следующем синке */
    }
  }

  /**
   * Гарантировать идентификатор устройства в meta.
   * Если его нет: на смартфоне (ширина < 768px) генерируем «говорящий»
   * `phone-xxxxxxxx`, чтобы админка показала бейдж «📱 Мобильная касса»;
   * на десктопе/планшете — стационарный `pos-terminal-1`.
   */
  public async ensureDeviceId(): Promise<string> {
    const existing = await this.getMeta(META_DEVICE_ID)
    if (existing) return existing
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
    const deviceId = isMobile ? `phone-${generateUUID().slice(0, 8)}` : 'pos-terminal-1'
    await this.setMeta(META_DEVICE_ID, deviceId)
    return deviceId
  }

  /**
   * Гарантировать идентификатор ЗОНЫ (рабочего места/отдела) в meta.
   *
   * Зона — общий пул вскрытой тары для всех устройств рабочего места. Если зоны
   * ещё нет, за неё принимается собственный device_id устройства (терминал сам
   * себе зона). При сопряжении телефон получает зону терминала, но сохраняет
   * СВОЙ device_id — сущности «зона» и «устройство» не смешиваются.
   */
  public async ensureZoneId(): Promise<string> {
    const existing = await this.getMeta(META_ZONE_ID)
    if (existing) return existing
    const deviceId = await this.ensureDeviceId()
    await this.setMeta(META_ZONE_ID, deviceId)
    return deviceId
  }

  /** Прочитать значение из meta (Dexie) с фолбэком на localStorage. */
  private async getMeta(key: string): Promise<string | null> {
    const row = await db.meta.get(key)
    if (row && typeof row.value === 'string') {
      return row.value
    }
    if (row && row.value != null) {
      return String(row.value)
    }
    return localStorage.getItem(`pos_meta_${key}`)
  }

  /** Записать значение в meta (Dexie) с фолбэком на localStorage. */
  private async setMeta(key: string, value: string): Promise<void> {
    try {
      await db.meta.put({ key, value })
    } catch {
      localStorage.setItem(`pos_meta_${key}`, value)
    }
  }
}

export const syncService = new SyncService()
